import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslationApp } from "../../../../lib/i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BORDER_RADIUS } from "../../../../utils/constants";
import {
  getAdsCampaignDemoDateRange,
  isAdsCampaignDemo,
} from "../../../../utils/adsCampaignDemoDateRange";
import { Skeleton } from "@mui/material";
import MetricsVisibilitySelector from "../../../../components/Common/MetricsVisibilitySelector";
import {
  computeActionMetrics,
  computeMultipleConversions,
  getCampaignMetricLabel,
  getGeneralMetricKeys,
  getMetricsToShow,
  mergeCampaignLevelStatistics,
} from "./campaignLevelMetrics";
import { renderMetricValue } from "../../../../utils/renderMetricValue";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../../../utils/colors";
import RangeDateField from "../../../../components/Form/RangeDateField";
import { useFormContext } from "react-hook-form";
import { useDispatchApp } from "../../../../lib/redux";
import _ from "lodash";
import {
  startGetStatisticsAdsCampaign,
  startSaveAdsCampaignMetricsConfiguration,
} from "../../../../actions/adsCampaigns";
import {
  METRICS,
  BUTTON_RESET_TO_BASIC_METRICS,
} from "../../../../i18n/keysTranslations";
import Button from "../../../../components/Buttons/Button";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import subWeeks from "date-fns/subWeeks";
import React from "react";
import { getObjectError } from "../../../../utils/string";

import { LaunchError } from "../../../../utils/errors";

const MetricCard = React.memo(function MetricCard({
  label,
  value,
  metric,
  currency,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        borderRadius: BORDER_RADIUS,
        backgroundColor: "fields.background",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        boxShadow: BOX_SHADOW_THEME,
        p: 1.5,
        width: 120,
      }}
    >
      <Typography
        variant="caption"
        color="fields.placeholder"
        sx={{
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2">
        {renderMetricValue(value, metric, currency)}
      </Typography>
    </Box>
  );
});

const StatisticsLevel = ({ platform = "general" }) => {
  const {
    campaignID,
    hasSomePlatformCreated,
    onChangeStatistics,
    onChangeIsLoadingStatistics,
    watch,
    dateFiltersForm,
    levelSectionSelected,
    sectionSelected,
    statistics: campaignStatistics,
    isLoadingStatistics,
    getIsCampaignPlatformCreated,
  } = useFormContext();
  const { t } = useTranslationApp();
  const [metricsVisibility, setMetricsVisibility] = useState({});
  const [configInitialized, setConfigInitialized] = useState(false);

  const {
    control: controlDateRange,
    setValue: setValueDateRange,
    getValues: getValuesDateRange,
    watch: watchDateRange,
  } = dateFiltersForm;
  const datePreset = watchDateRange("datePreset");

  const isPlatformCreated = getIsCampaignPlatformCreated(platform);

  const dispatch = useDispatchApp();
  const firstActiveTime = watch("firstActiveTime");
  const platformData = watch(`platformsData.${platform}`) || {};
  const conversionActions = platformData?.conversionActions;

  const getLabel = (metric) => getCampaignMetricLabel(metric, platform, t);

  const onChangeDateRange = async (dateRangeOverride) => {
    onChangeIsLoadingStatistics(true);
    const dateRange = dateRangeOverride || getValuesDateRange("dateRange");

    if (
      _.isEmpty(dateRange) ||
      !_.isDate(dateRange.startDateTime) ||
      !_.isDate(dateRange.endDateTime)
    ) {
      onChangeIsLoadingStatistics(false);
      return;
    }
    const response = await dispatch(
      startGetStatisticsAdsCampaign({
        campaignID,
        startDateTime: new Date(dateRange.startDateTime).getTime(),
        endDateTime: new Date(dateRange.endDateTime).getTime(),
      })
    );

    if (response.ok) {
      const totalStatistics = response?.data || {};
      onChangeStatistics(totalStatistics);
    }
    onChangeIsLoadingStatistics(false);
  };

  const onGetStatisticsConfiguration = async () => {
    const effectiveFirstActiveTime =
      firstActiveTime || (isPlatformCreated ? Date.now() : null);

    if (!effectiveFirstActiveTime && !isAdsCampaignDemo(campaignID)) {
      onChangeIsLoadingStatistics(false);
      return;
    }

    onChangeIsLoadingStatistics(true);

    if (isAdsCampaignDemo(campaignID)) {
      const demoDateRange = getAdsCampaignDemoDateRange();
      setValueDateRange("dateRange.startDateTime", demoDateRange.startDateTime);
      setValueDateRange("dateRange.endDateTime", demoDateRange.endDateTime);
      onChangeDateRange({
        startDateTime: demoDateRange.startDateTime,
        endDateTime: demoDateRange.endDateTime,
      });
      return;
    }

    const now = new Date();
    const twoWeeksAgo = subWeeks(now, 2);

    const startDateTime =
      effectiveFirstActiveTime > twoWeeksAgo.getTime()
        ? effectiveFirstActiveTime
        : twoWeeksAgo.getTime();

    const startDate = new Date(startDateTime);
    setValueDateRange("dateRange.startDateTime", startDate);
    setValueDateRange("dateRange.endDateTime", now);
    onChangeDateRange({
      startDateTime: startDate,
      endDateTime: now,
    });
  };

  const onChangeDatePreset = useCallback(
    (presetKey) => {
      setValueDateRange("datePreset", presetKey || "");
    },
    [setValueDateRange]
  );

  const getStatisticsLevel = useCallback(() => {
    const totalStatistics = campaignStatistics?.total || {};

    if (levelSectionSelected === "platform") {
      return totalStatistics?.[sectionSelected.platform]?.campaign;
    }
    if (levelSectionSelected === "assetsGroup") {
      return totalStatistics?.[sectionSelected.platform]?.assetsGroups?.[
        sectionSelected.assetsGroupID
      ];
    }
    if (levelSectionSelected === "adGroup") {
      return totalStatistics?.[sectionSelected.platform]?.adsGroups?.[
        sectionSelected.adGroupID
      ];
    }
    if (levelSectionSelected === "ad") {
      return totalStatistics?.[sectionSelected.platform]?.ads?.[
        sectionSelected.adID
      ];
    }

    return {};
  }, [campaignStatistics, levelSectionSelected, sectionSelected]);

  const rawStatisticsLevel = useMemo(
    () => getStatisticsLevel() || {},
    [getStatisticsLevel]
  );

  const { computed: actionMetricsValues, keys: actionMetricsKeys } = useMemo(
    () => computeActionMetrics(rawStatisticsLevel, platform),
    [rawStatisticsLevel, platform]
  );

  const {
    computed: multipleConversionsComputed,
    keys: multipleConversionsKeys,
  } = useMemo(
    () =>
      computeMultipleConversions(
        rawStatisticsLevel,
        platform,
        conversionActions
      ),
    [rawStatisticsLevel, platform, conversionActions]
  );

  const metricsToShow = useMemo(
    () =>
      getMetricsToShow({
        platform,
        multipleConversionsKeys,
        actionMetricsKeys,
      }),
    [actionMetricsKeys, multipleConversionsKeys, platform]
  );

  const statisticsLevel = useMemo(
    () =>
      mergeCampaignLevelStatistics({
        metricsToShow,
        raw: rawStatisticsLevel,
        actionMetricsValues,
        multipleConversionsComputed,
      }),
    [
      metricsToShow,
      rawStatisticsLevel,
      actionMetricsValues,
      multipleConversionsComputed,
    ]
  );

  // Filter metrics based on visibility - memoized to react to metricsVisibility changes
  const visibleMetrics = useMemo(() => {
    return metricsToShow.filter((metric) => {
      return metricsVisibility[metric] !== false;
    });
  }, [metricsToShow, metricsVisibility]);

  const onResetMetrics = async () => {
    const allGeneralMetrics = getGeneralMetricKeys();

    // Create new configuration: all metrics false, only general metrics true
    const resetConfiguration = {};
    metricsToShow.forEach((metric) => {
      resetConfiguration[metric] = false;
    });

    allGeneralMetrics.forEach((metric) => {
      if (metricsToShow.includes(metric)) {
        resetConfiguration[metric] = true;
      }
    });

    setMetricsVisibility(resetConfiguration);

    // Save to DB
    try {
      await dispatch(
        startSaveAdsCampaignMetricsConfiguration({
          campaignID,
          platform,
          metrics: resetConfiguration,
        })
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onResetMetrics",
          capture: "exception",
        })
      );
      console.error("Error saving reset metrics configuration:", error);
    }
  };

  useEffect(() => {
    if (!isPlatformCreated) {
      return;
    }
    if (!_.isEmpty(campaignStatistics) || isLoadingStatistics) {
      return;
    }
    onGetStatisticsConfiguration();

    // eslint-disable-next-line
  }, [isPlatformCreated, firstActiveTime, campaignID]);

  // Reset configInitialized when level changes to show skeletons while loading new config
  useEffect(() => {
    setConfigInitialized(false);
    setMetricsVisibility({});
  }, [levelSectionSelected]);

  if (!isPlatformCreated) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        p: 2,
        borderRadius: BORDER_RADIUS,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AnalyticsIcon />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "1.05rem" }}
          >
            {t(METRICS)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {hasSomePlatformCreated && (
            <RangeDateField
              showStartNowButton={false}
              sx={{
                ml: 1,
                p: 1,
                borderRadius: BORDER_RADIUS,
                textDecoration: "underline",
                textDecorationColor: "rgba(0,0,0,0.5)",
                textUnderlineOffset: "3.5px",
              }}
              control={controlDateRange}
              name="dateRange"
              mode="text"
              onSave={onChangeDateRange}
              initialPresetKey={datePreset || null}
              onPresetChange={onChangeDatePreset}
            />
          )}
          {/* Configuration button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={onResetMetrics}
              size="small"
              sx={{
                minWidth: "auto",
                height: 32,
                fontSize: "0.75rem",
                px: 1.5,
              }}
            >
              {t(BUTTON_RESET_TO_BASIC_METRICS)}
            </Button>

            <MetricsVisibilitySelector
              availableMetrics={metricsToShow}
              metricsVisibility={metricsVisibility}
              setMetricsVisibility={setMetricsVisibility}
              getLabel={getLabel}
              isFromStatisticsLevel={true}
              campaignID={campaignID}
              platform={platform}
              levelSectionSelected={levelSectionSelected}
              onInitializationComplete={() => {
                setConfigInitialized(true);
              }}
            />
          </Box>
        </Box>
      </Box>
      {!isLoadingStatistics &&
        configInitialized &&
        !_.isEmpty(rawStatisticsLevel?.error) && (
          <Typography variant="caption" color="error">
            {getObjectError(rawStatisticsLevel.error).message}
          </Typography>
        )}
      {/* Visible metrics */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Show skeletons while loading initial configuration or data */}
        {isLoadingStatistics || !configInitialized ? (
          // Show skeletons for the metrics that will be shown
          Array(7)
            .fill("")
            .map((_, idx) => (
              <Skeleton key={idx} variant="rounded" width={120} height={64} />
            ))
        ) : (
          // Show real metrics only when the configuration is initialized
          <>
            {visibleMetrics.map((metric) => {
              const value = statisticsLevel[metric];
              const label = getLabel(metric);
              return (
                <MetricCard
                  key={metric}
                  metric={metric}
                  value={value}
                  label={label}
                  currency={statisticsLevel?.currency}
                />
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
};

export default StatisticsLevel;
